// Core library dependencies
import { ApiResponse, API_SUCCESS_STATUS_CODE, TEXTS, isApiResponse } from "@swiggy-private/core-utils";

// Types
import { HTTP_METHODS } from "@swiggy-private/checkout-core";
import { OrderApiData } from "@swiggy-private/payment-types";
import {
    initiateInstamartPayment,
    confirmInstamartPayment,
    InstamartPaymentResponse,
    PaymentTransactionState,
} from "@payment-client/apis/instamartPaymentService";
import { ConfirmOrderPublicParams, OrderPlaceParams } from "@payment-client/interfaces/OrderPublic";

// Utils
import {
    buildInstamartPayOnDeliveryInitiateRequestBody,
    transformInstamartPayOnDeliveryPaymentResponse,
    buildInstamartPayOnDeliveryConfirmRequestBody,
} from "@payment-client/helpers/InstamartPayOnDeliveryUtils";
import {
    getOrderTxnIdFromOrderData,
    isEligibleForNewPendingPaymentFlow,
} from "@payment-client/helpers/publicOrderExtensions";
import { getPendingPaymentConfirmRequestObj } from "@payment-client/helpers/paymentPendingUtils";

function getInstamartPaymentError(
    response: ApiResponse<InstamartPaymentResponse> | InstamartPaymentResponse,
): Pick<ApiResponse, "statusCode" | "statusMessage"> | null {
    if (isApiResponse<InstamartPaymentResponse>(response)) {
        if (response.statusCode !== API_SUCCESS_STATUS_CODE) {
            return { statusCode: response.statusCode, statusMessage: response.statusMessage };
        }

        return null;
    }

    const paymentState = response.payment?.state;

    if (
        !paymentState ||
        paymentState === PaymentTransactionState.STATE_FAIL ||
        paymentState === PaymentTransactionState.STATE_INVALID
    ) {
        return { statusCode: 1, statusMessage: TEXTS.OOPS_SOMETHING_WENT_WRONG };
    }

    return null;
}

// Place order for DASH POD using Instamart Shield APIs
export async function placeDashPODOrder(
    apiHost: string,
    params: OrderPlaceParams,
    headers: Record<string, string>,
    options?: { isConfirmOnAppLaunchEnabled?: boolean },
): Promise<ApiResponse<OrderApiData>> {
    const { isConfirmOnAppLaunchEnabled } = options ?? {};
    const orderId = (headers?.["orderid"] as string) || "";

    const requestBody = buildInstamartPayOnDeliveryInitiateRequestBody(orderId, params);

    const response = await initiateInstamartPayment(apiHost, requestBody, headers);

    const paymentError = getInstamartPaymentError(response);
    if (paymentError !== null) {
        return { statusCode: paymentError.statusCode, statusMessage: paymentError.statusMessage, data: null };
    }

    const orderApiData = transformInstamartPayOnDeliveryPaymentResponse(orderId, response);

    const isEligibleForNewFlow = isEligibleForNewPendingPaymentFlow(
        headers["platform"] as string,
        headers["version-code"] as string,
    );

    // Add pending payment params if eligible
    if (orderApiData.orderData != null && isEligibleForNewFlow) {
        // get orderId, transactionId from response object
        const pendingRequestBody = getOrderTxnIdFromOrderData(orderApiData.orderData);

        // Send Pending payment params in order to invoke native confirm order flow when app is killed
        const pendingPaymentParams = getPendingPaymentConfirmRequestObj({
            headers,
            method: HTTP_METHODS.POST,
            body: pendingRequestBody,
            isConfirmOnAppLaunchEnabled,
        });

        return {
            statusCode: API_SUCCESS_STATUS_CODE,
            data: orderApiData,
            pendingPaymentParams,
        };
    }

    return {
        statusCode: API_SUCCESS_STATUS_CODE,
        data: orderApiData,
    };
}

// Confirm order for DASH POD using Instamart Shield APIs
export async function confirmDashPODOrder(
    apiHost: string,
    params: ConfirmOrderPublicParams,
    headers: Record<string, string>,
): Promise<ApiResponse<OrderApiData>> {
    const orderId = (headers["orderid"] as string) || "";
    const paymentId = params.transactionId || "";

    const requestBody = buildInstamartPayOnDeliveryConfirmRequestBody(orderId, paymentId);

    const response = await confirmInstamartPayment(apiHost, requestBody, headers);

    const paymentError = getInstamartPaymentError(response);
    if (paymentError !== null) {
        return { statusCode: paymentError.statusCode, statusMessage: paymentError.statusMessage, data: null };
    }

    return {
        statusCode: API_SUCCESS_STATUS_CODE,
        data: transformInstamartPayOnDeliveryPaymentResponse(orderId, response),
    };
}
